import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../../contexts/ThemeContext';
import { SendDestination } from '../../types/sendTo';
import {
  FiPackage, FiFileText, FiGitBranch, FiX, FiUser,
  FiCopy, FiDownload, FiCheck, FiEye, FiEdit2
} from 'react-icons/fi';

interface SendToModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialContent: string;
  isLoadingSummary: boolean;
  onConfirm: (content: string, destination: SendDestination) => void;
  isSending: boolean;
  error: string | null;
  // New props
  sessionId: string;
  personaName: string;
  defaultDestination?: SendDestination;
}

export const SendToModal: React.FC<SendToModalProps> = ({
  isOpen,
  onClose,
  initialContent,
  isLoadingSummary,
  onConfirm,
  isSending,
  error,
  sessionId,
  personaName,
  defaultDestination,
}) => {
  const { darkMode } = useTheme();

  // Editor state
  const [content, setContent] = useState('');
  const [mode, setMode] = useState<'edit' | 'preview'>('preview');
  const [hasEdited, setHasEdited] = useState(false);

  // Discard confirm overlay
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Toolbar flash states
  const [copyFlash, setCopyFlash] = useState(false);
  const [downloadFlash, setDownloadFlash] = useState(false);

  // Destination
  const [destination, setDestination] = useState<SendDestination>(
    defaultDestination ?? 'artifactory_project'
  );

  // Card pulse animation
  const [pulsing, setPulsing] = useState<SendDestination | null>(null);

  // Sync content when initialContent changes (summary arrives)
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  // Reset all state (except content) when modal opens
  // NOTE: mode must default to 'preview' here — do NOT set 'edit'.
  // The modal opens before the summary arrives, so setting 'edit' here
  // would race against the useState('preview') default and stomp it.
  useEffect(() => {
    if (isOpen) {
      setMode('preview');
      setHasEdited(false);
      setShowDiscardConfirm(false);
      setCopyFlash(false);
      setDownloadFlash(false);
      setDestination(defaultDestination ?? 'artifactory_project');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  // Stats
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const charCount = content.length;
  const tokenEstimate = Math.ceil(content.length / 4);
  const tokenColor =
    tokenEstimate >= 4000
      ? 'text-red-500'
      : tokenEstimate >= 2000
      ? 'text-yellow-500'
      : darkMode ? 'text-gray-400' : 'text-gray-500';

  // Handlers
  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopyFlash(true);
    setTimeout(() => setCopyFlash(false), 1500);
  };

  const handleDownload = () => {
    const date = new Date().toISOString().split('T')[0];
    const safeName = personaName.toLowerCase().replace(/\s+/g, '-');
    const filename = `summary-${safeName}-${date}.md`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    setDownloadFlash(true);
    setTimeout(() => setDownloadFlash(false), 1500);
  };

  const handleSelectDestination = (key: SendDestination) => {
    setDestination(key);
    setPulsing(key);
    setTimeout(() => setPulsing(null), 300);
  };

  const handleRequestClose = () => {
    if (hasEdited) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirmed = () => {
    setShowDiscardConfirm(false);
    setHasEdited(false);
    onClose();
  };

  const isConfirmDisabled = !content.trim() || isLoadingSummary || isSending;

  const DESTINATIONS: {
    key: SendDestination;
    label: string;
    description: string;
    icon: React.ReactNode;
    disabled?: boolean;
  }[] = [
    {
      key: 'artifactory_project',
      label: 'New Artifactory Project',
      description: 'Scaffold a full project blueprint from this conversation using AI.',
      icon: <FiPackage className="w-5 h-5" />,
    },
    {
      key: 'artifact',
      label: 'New Artifact',
      description: 'Save as a versioned document artifact in your library.',
      icon: <FiFileText className="w-5 h-5" />,
    },
    {
      key: 'workflow',
      label: 'New Workflow',
      description: 'Bootstrap a workflow design from this conversation.',
      icon: <FiGitBranch className="w-5 h-5" />,
    },
    {
      key: 'forge' as SendDestination,
      label: 'Persona Forge',
      description: 'Send as genesis intent or source mutation for persona engineering.',
      icon: <FiUser className="w-5 h-5" />,
    },
  ];

  // Per-destination accent classes
  const accentClasses = (key: SendDestination, selected: boolean) => {
    if (!selected) {
      return darkMode
        ? 'border-gray-600 hover:border-gray-500 text-gray-300'
        : 'border-gray-200 hover:border-gray-300 text-gray-700';
    }
    if (key === 'artifactory_project') {
      return darkMode
        ? 'border-purple-500 bg-purple-900/30 text-purple-300'
        : 'border-purple-500 bg-purple-50 text-purple-700';
    }
    if (key === 'artifact') {
      return darkMode
        ? 'border-green-500 bg-green-900/30 text-green-300'
        : 'border-green-500 bg-green-50 text-green-700';
    }
    return darkMode
      ? 'border-blue-500 bg-blue-900/30 text-blue-300'
      : 'border-blue-500 bg-blue-50 text-blue-700';
  };

  const iconAccentClass = (key: SendDestination) => {
    if (key === 'artifactory_project') return darkMode ? 'text-purple-400' : 'text-purple-600';
    if (key === 'artifact') return darkMode ? 'text-green-400' : 'text-green-600';
    return darkMode ? 'text-gray-500' : 'text-gray-400';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleRequestClose} />

      {/* Modal container */}
      <div className={`relative w-full max-w-4xl h-[85vh] flex flex-col rounded-xl shadow-2xl overflow-hidden ${
        darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      }`}>

        {/* ── HEADER ── */}
        <div className={`flex items-center justify-between px-5 py-3 border-b flex-shrink-0 ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h3 className="text-lg font-semibold">Send Conversation</h3>
          <button
            onClick={handleRequestClose}
            className={`p-1.5 rounded-md transition-colors ${
              darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* ── BODY ── */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row min-h-0">

          {/* LEFT PANEL — editor */}
          <div className={`flex-1 lg:w-[65%] flex flex-col p-4 min-h-0 ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          } lg:border-r`}>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-3 flex-shrink-0">
              {/* Edit / Preview toggle */}
              <div className={`flex rounded-md overflow-hidden border ${
                darkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <button
                  onClick={() => setMode('edit')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-all duration-200 ${
                    mode === 'edit'
                      ? 'bg-blue-600 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 shadow-[0_0_8px_rgba(59,130,246,0.5)] ring-1 ring-blue-500/50'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 shadow-[0_0_6px_rgba(59,130,246,0.35)] ring-1 ring-blue-400/40'
                  }`}
                >
                  <FiEdit2 className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setMode('preview')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                    mode === 'preview'
                      ? 'bg-blue-600 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FiEye className="w-3.5 h-3.5" />
                  Preview
                </button>
              </div>

              <div className="flex-1" />

              {/* Copy button */}
              <button
                onClick={handleCopy}
                disabled={!content}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                }`}
              >
                {copyFlash ? <FiCheck className="w-3.5 h-3.5 text-green-400" /> : <FiCopy className="w-3.5 h-3.5" />}
                {copyFlash ? 'Copied!' : 'Copy'}
              </button>

              {/* Download button */}
              <button
                onClick={handleDownload}
                disabled={!content.trim()}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                }`}
              >
                {downloadFlash ? <FiCheck className="w-3.5 h-3.5 text-green-400" /> : <FiDownload className="w-3.5 h-3.5" />}
                {downloadFlash ? 'Downloaded!' : 'Download .md'}
              </button>
            </div>

            {/* Editor area */}
            <div className="flex-1 overflow-hidden min-h-0">
              {isLoadingSummary ? (
                <div className={`h-full rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" />
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0.4s' }} />
                    </div>
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Generating summary...
                    </span>
                  </div>
                </div>
              ) : mode === 'edit' ? (
                <textarea
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    setHasEdited(true);
                  }}
                  className={`w-full h-full rounded-lg px-3 py-2 text-sm resize-y min-h-[12rem] ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Summary content to send..."
                />
              ) : (
                <div className={`w-full h-full overflow-y-auto rounded-lg px-4 py-3 text-sm ${
                  darkMode ? 'bg-gray-900/60' : 'bg-gray-50'
                }`}>
                  <article className="changelog-prose max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        // LLM-generated summary content — every link
                        // opens in a new tab so the user doesn't lose
                        // the modal context they're previewing in.
                        a: ({ node, href, children, ...props }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {content || '*Nothing to preview.*'}
                    </ReactMarkdown>
                  </article>
                </div>
              )}
            </div>

            {/* Stats bar */}
            <div className={`mt-2 text-xs text-right flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {wordCount} words · {charCount} chars ·{' '}
              <span className={tokenColor}>~{tokenEstimate} tokens</span>
            </div>
          </div>

          {/* RIGHT PANEL — destination picker */}
          <div className="lg:w-[35%] flex flex-col p-4 flex-shrink-0">
            <div className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Send to:
            </div>

            <div className="space-y-3">
              {DESTINATIONS.map((dest) => {
                const isSelected = destination === dest.key && !dest.disabled;
                return (
                  <button
                    key={dest.key}
                    onClick={() => !dest.disabled && handleSelectDestination(dest.key)}
                    disabled={dest.disabled}
                    className={`relative w-full flex items-start p-3 rounded-lg border text-left transition-all duration-150 ${
                      dest.disabled
                        ? 'opacity-40 cursor-not-allowed ' + (darkMode ? 'border-gray-700' : 'border-gray-200')
                        : accentClasses(dest.key, isSelected)
                    } ${pulsing === dest.key ? 'scale-105' : 'scale-100'}`}
                  >
                    {/* Checkmark badge */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <FiCheck className="w-3 h-3 text-white" />
                      </div>
                    )}

                    <div className={`mr-3 mt-0.5 flex-shrink-0 ${dest.disabled ? (darkMode ? 'text-gray-600' : 'text-gray-400') : iconAccentClass(dest.key)}`}>
                      {dest.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{dest.label}</span>
                        {dest.disabled && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-600 text-gray-300">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {dest.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Error display */}
            {error && (
              <div className={`mt-4 text-sm px-3 py-2 rounded-lg ${
                darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
              }`}>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className={`flex justify-end gap-2 px-5 py-3 border-t flex-shrink-0 ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={handleRequestClose}
            className={`px-4 py-2 rounded-md text-sm transition-colors ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(content, destination)}
            disabled={isConfirmDisabled}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
              isConfirmDisabled
                ? 'bg-blue-400 cursor-not-allowed opacity-50'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            Confirm &amp; Go
          </button>
        </div>

        {/* ── DISCARD CONFIRM OVERLAY ── */}
        {showDiscardConfirm && (
          <div className="absolute inset-0 z-20 bg-black/40 flex items-center justify-center rounded-xl">
            <div className={`px-6 py-5 rounded-xl shadow-xl max-w-sm w-full mx-4 ${
              darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}>
              <p className="text-base font-semibold mb-1">Discard your edits?</p>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Your changes will be lost.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDiscardConfirm(false)}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Keep Editing
                </button>
                <button
                  onClick={handleDiscardConfirmed}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
                >
                  Discard &amp; Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
